import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createAccountPowerSyncQuery } from "@/lib/powersync/create-account-query";
import { keepPreviousData } from "@tanstack/solid-query";
import {
  addAttachmentMutation,
  cn,
  deleteAttachmentMutation,
  formatBytes,
  getAttachmentsByTodoQuery,
  MAX_ATTACHMENT_SIZE_BYTES,
  queryKeys,
} from "@listto/shared";
import type { AttachmentRecord, PowerSyncDrizzleDB } from "@listto/powersync";
import { Loader2, Paperclip, Plus, Upload } from "@fleet/icons";
import { Badge } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { downloadStorageObject, uploadStorageObject } from "@/lib/api";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { FileCard } from "./file-card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

interface AttachmentsListProps {
  todoId: string;
}

const handleDragOver = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

/**
 * Uploads a file to storage and persists it as an attachment row.
 *
 * Extracted from `processUpload` so it's testable without rendering the
 * Solid component: it deliberately persists the bare storage **key**, not
 * the absolute URL the upload endpoint also returns, because this row syncs
 * across devices/backends (`resolveStorageUrl`/`buildStorageUrl` already
 * expand a bare key against whichever backend origin is CURRENT at read
 * time — see `apps/web/src/lib/api.ts` and
 * `packages/shared/src/utils/server-url.ts`).
 */
export async function uploadAndPersistAttachment(params: {
  db: PowerSyncDrizzleDB;
  todoId: string;
  file: File;
}): Promise<string> {
  const { db, todoId, file } = params;
  const { key } = await uploadStorageObject(file);
  return addAttachmentMutation({
    db,
    todoId,
    filename: file.name,
    url: key,
    contentType: file.type || "application/octet-stream",
    size: file.size,
  });
}

export function AttachmentsList(props: AttachmentsListProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const [isUploading, setIsUploading] = createSignal(false);
  const [downloadingId, setDownloadingId] = createSignal<string | null>(null);
  const [isDragging, setIsDragging] = createSignal(false);
  const [expandedId, setExpandedId] = createSignal<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = createSignal<string | null>(null);
  const [fileInputRef, setFileInputRef] = createSignal<HTMLInputElement | null>(null);

  const attachments = createAccountPowerSyncQuery<AttachmentRecord>({
    queryKey: () => queryKeys.attachments.byTodo(props.todoId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getAttachmentsByTodoQuery(dbInstance, props.todoId) : null;
    },
    enabled: () => Boolean(props.todoId) && Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const processUpload = async (file: File) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      const size = formatBytes(MAX_ATTACHMENT_SIZE_BYTES);
      showToast(t(msg`File is too large. Maximum size is ${size}.`), "error");
      return;
    }

    setIsUploading(true);
    try {
      await uploadAndPersistAttachment({ db: dbInstance, todoId: props.todoId, file });
    } catch (err) {
      console.error("Error uploading attachment:", err);
      showToast(t(msg`Failed to upload attachment. Please try again.`), "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (e: Event) => {
    const { target } = e;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    const file = target.files?.[0];
    if (file) {
      await processUpload(file);
    }
    target.value = "";
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading()) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isUploading()) {
      return;
    }

    const file = e.dataTransfer?.files[0];
    if (file) {
      await processUpload(file);
    }
  };

  const handleDownload = async (attachment: AttachmentRecord) => {
    if (downloadingId()) {
      return;
    }
    setDownloadingId(attachment.id);
    try {
      await downloadStorageObject(attachment.url, attachment.filename ?? "attachment");
    } catch (err) {
      console.error("Error downloading attachment:", err);
      showToast(t(msg`Failed to download attachment. Please try again.`), "error");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await deleteAttachmentMutation(dbInstance, id);
      if (expandedId() === id) {
        setExpandedId(null);
      }
    } catch (err) {
      console.error("Error deleting attachment:", err);
    }
  };

  return (
    <div
      class="space-y-3 relative rounded-lg transition-all duration-200 w-full min-w-0"
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Show when={isDragging()}>
        <div class="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex flex-col items-center justify-center gap-2 z-20 backdrop-blur-[2px] transition-all duration-300 pointer-events-none animate-in fade-in">
          <Upload class="h-6 w-6 text-primary animate-bounce" />
          <span class="text-xs font-semibold text-primary">{t(msg`Drop to upload`)}</span>
        </div>
      </Show>

      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Paperclip class="h-4 w-4" />
          {t(msg`Attachments`)}
          <Show when={attachments.data?.length}>
            <Badge variant="secondary" class="ml-1 px-1.5 py-0 h-4 text-[10px]">
              {attachments.data?.length}
            </Badge>
          </Show>
        </div>

        <label
          tabindex="0"
          aria-label={t(msg`Add attachment`)}
          title={t(msg`Add attachment`)}
          class="group/attach focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full cursor-pointer transition-shadow"
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              const input = e.currentTarget.querySelector('input[type="file"]');
              if (input instanceof HTMLInputElement) {
                input.click();
              }
            }
          }}
        >
          <input
            ref={setFileInputRef}
            type="file"
            class="hidden"
            onChange={handleFileUpload}
            disabled={isUploading() || !drizzle()}
          />
          <div class="flex items-center gap-2">
            <kbd
              class="h-4.5 min-w-[15px] inline-flex items-center justify-center rounded border border-primary/20 bg-primary/5 px-1.5 font-mono text-[9px] font-bold text-primary opacity-0 group-hover/attach:opacity-100 group-focus-within/attach:opacity-100 transition-opacity select-none cursor-help"
              title={t(msg`Add attachment: Press A to upload an attachment`)}
            >
              {t(msg`A`)}
            </kbd>
            <div class="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
              <Plus class={cn("h-4 w-4", isUploading() && "animate-spin")} />
            </div>
          </div>
        </label>
      </div>

      <div class="grid gap-2 w-full min-w-0">
        <For each={attachments.data}>
          {(attachment) => (
            <FileCard
              name={attachment.filename ?? "File"}
              size={attachment.size}
              contentType={attachment.content_type}
              uploadedAt={attachment.created_at}
              isDownloading={downloadingId() === attachment.id}
              isExpanded={expandedId() === attachment.id}
              onToggleExpand={() => {
                toggleExpand(attachment.id);
              }}
              onDownload={() => void handleDownload(attachment)}
              onDelete={() => {
                setDeleteConfirmId(attachment.id);
              }}
            />
          )}
        </For>

        <Show when={!attachments.isLoading && attachments.data?.length === 0 && !isUploading()}>
          <button
            type="button"
            onClick={() => fileInputRef()?.click()}
            class="w-full py-4 text-center text-xs text-muted-foreground border border-dashed border-border rounded-lg hover:border-primary/50 hover:text-foreground hover:bg-muted/30 transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {t(msg`No attachments yet`)}
          </button>
        </Show>

        <Show when={isUploading()}>
          <div class="flex items-center gap-3 rounded-lg border border-border bg-card p-3 animate-pulse">
            <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted">
              <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
            <div class="flex-1 space-y-2">
              <div class="h-3 w-1/2 bg-muted rounded" />
              <div class="h-2 w-1/4 bg-muted rounded" />
            </div>
          </div>
        </Show>
      </div>

      <ConfirmDialog
        open={Boolean(deleteConfirmId())}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirmId(null);
          }
        }}
        title={t(msg`Delete attachment`)}
        description={t(
          msg`Are you sure you want to delete this attachment? This action cannot be undone.`,
        )}
        variant="destructive"
        confirmLabel={t(msg`Delete`)}
        onConfirm={() => {
          const id = deleteConfirmId();
          if (id) {
            void handleDelete(id);
            setDeleteConfirmId(null);
          }
        }}
      />
    </div>
  );
}
